import { useMutation, useQuery } from "@tanstack/react-query";
import type { PublicShareForm, PublicSubmitBody } from "@guestbook/shared";
import { apiFetch, type ApiClientError } from "@/shared/lib/api";

/**
 * Loads the public share form for a token. Unauthenticated: the endpoint
 * returns 404 (not 401) for missing/revoked/expired tokens, so apiFetch's
 * auth-refresh path never fires. `retry: false` so an inactive link surfaces
 * its 404 immediately instead of retrying.
 */
export function usePublicShareForm(token: string | undefined) {
  return useQuery<PublicShareForm, ApiClientError>({
    queryKey: ["publicShare", token],
    queryFn: () => apiFetch<PublicShareForm>(`/public/share/${token}`),
    enabled: Boolean(token),
    retry: false,
  });
}

/** Check an access code so the page can reveal the form. 403 on a wrong code. */
export function useVerifyAccessCode(token: string | undefined) {
  return useMutation<{ ok: boolean }, ApiClientError, string>({
    mutationFn: (accessCode) =>
      apiFetch<{ ok: boolean }>(`/public/share/${token}/verify`, {
        method: "POST",
        body: JSON.stringify({ accessCode }),
      }),
  });
}

export function useSubmitPublicShare(token: string | undefined) {
  return useMutation<{ ok: boolean }, ApiClientError, PublicSubmitBody>({
    mutationFn: (body) =>
      apiFetch<{ ok: boolean }>(`/public/share/${token}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}
